import { transformSearch } from "@/api/helper.ts";
import { queryClient } from "@/query-client.ts";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import caseEvidenceService from "../services/evidence-service";

export const caseEvidenceSearchQuery = z.object({
	search: z.string().optional(),
	parties: z.string().optional(),
	case_id: z.string().optional(),
	id: z.string().optional(),
	case_record_user_depth: z.string().optional().nullable(),
	case_record_user_id: z.string().optional().nullable(),
	case_activity_ids: z.string().optional().nullable(),
	parent_id: z.string().optional().nullable(),
	evidence_type: z.string().optional(),
	status: z.string().optional().nullable(),
	include: z.string().optional(),
	per_page: z.number().optional(),
	page: z.number().nullable().optional(),
	category: z.string().optional().nullable(),
});

export const caseEvidenceQueryOptions = (search?: z.infer<typeof caseEvidenceSearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_evidence", search],
		queryFn: () =>
			caseEvidenceService.listByCaseId(
				search?.case_id,
				transformSearch(search, [
					"parties",
					"evidence_type",
					"case_id",
					"search",
					"case_record_user_depth",
					"case_record_user_id",
					"id",
					"parent_id",
					"status",
					"category",
				]),
			),
		...options,
	});
};
export const caseEvidenceListAllQueryOptions = (search?: z.infer<typeof caseEvidenceSearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_evidence", search],
		queryFn: () =>
			caseEvidenceService.listAllByCaseId(
				search?.case_id,
				transformSearch(search, ["parties", "evidence_type", "case_id", "search", "case_record_user_depth", "case_record_user_id", "id", "parent_id"]),
			),
		...options,
	});
};

// export const useCaseEvidenceMutation = (evidenceId?: string) => {
// 	return useMutation({
// 		mutationKey: ["case_evidence", evidenceId],
// 		mutationFn: caseEvidenceService.createById,
// 		meta: {
// 			successMessage: "Evidence saved successfully",
// 		},
// 		onSuccess: () =>
// 			queryClient.invalidateQueries({ queryKey: ["case_evidence"] }),
// 	});
// };

export const useCaseEvidenceMutation = (evidenceId?: string) => {
	return useMutation({
		mutationKey: ["case_evidence", evidenceId],
		mutationFn: caseEvidenceService.upsertById,
		meta: {
			successMessage: "Evidence saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_evidence"] });
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
		},
	});
};

export const useEvidenceMutation = (evidenceId?: string) => {
	return useMutation({
		mutationKey: ["case_evidence", evidenceId],
		mutationFn: caseEvidenceService.upsert,
		meta: {
			successMessage: "Evidence saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_evidence"] });
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
		},
	});
};

export const useEvidenceSubmarkingMutation = (evidenceId?: string) => {
	return useMutation({
		mutationKey: ["case_evidence", evidenceId],
		mutationFn: caseEvidenceService.addSubmarking,
		meta: {
			successMessage: "Evidence added successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_evidence"] });
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
		},
	});
};
export const useEvidenceMultipleSubmarkingMutation = (evidenceId?: string) => {
	return useMutation({
		mutationKey: ["case_evidence", evidenceId],
		mutationFn: caseEvidenceService.addMultipleSubmarking,
		meta: {
			// successMessage: "Evidence added successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_evidence"] });
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
		},
	});
};
export const useTestimonialEvidenceMutation = (evidenceId?: string) => {
	//testimonial modal
	return useMutation({
		mutationKey: ["case_evidence", evidenceId],
		mutationFn: caseEvidenceService.upsertTestimony,
		meta: {
			successMessage: "Evidence saved successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_evidence"] });
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
		},
	});
};

export const useCaseEvidenceDeleteMutation = () => {
	return useMutation({
		mutationKey: ["case_evidence", "delete"],
		mutationFn: caseEvidenceService.deleteById,
		meta: {
			successMessage: "Evidence archived successfully",
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["case_evidence"] });
			queryClient.invalidateQueries({ queryKey: ["case_records"] });
			queryClient.invalidateQueries({ queryKey: ["case_activities"] });
		},
	});
};

export const useCaseEvidenceListDownload = () => {
	return useMutation({
		mutationFn: () => caseEvidenceService.downloadListByType,
		meta: {
			successMessage: "Downloaded Evidence List",
		},
		onSuccess: () => {
			// do nothing
		},
	});
};
export const caseEvidenceListTsnQueryOptions = (search?: z.infer<typeof caseEvidenceSearchQuery>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["case_evidence", search],
		queryFn: () =>
			caseEvidenceService.listTsnEvidence(
				search?.case_id,
				transformSearch(search, ["parties", "evidence_type", "case_id", "search", "case_record_user_depth", "case_record_user_id", "id", "parent_id"]),
			),
		...options,
	});
};
